/**
 * v1.10.0 — réglages lus au chargement de la page.
 *
 * Une release se construit une fois et s'installe sur n'importe quel serveur :
 * l'installation écrit `config.js` à côté de `index.html`
 * (`window.__STREAMING_CONFIG__`). Ordre de priorité : `config.js`, puis les
 * variables `VITE_*` de la construction, puis des valeurs de la même origine.
 * Aucune adresse de serveur n'est écrite dans le code.
 */

export interface RuntimeConfig {
  apiBaseUrl: string;
  wsBaseUrl: string;
  keycloakRealmUrl: string;
  keycloakClientId: string;
  authRedirectUri: string;
}

export type RawRuntimeConfig = Partial<Record<keyof RuntimeConfig, unknown>>;

declare global {
  interface Window {
    __STREAMING_CONFIG__?: RawRuntimeConfig;
  }
}

/** Realm et client créés par `deploy/install.sh`. */
export const DEFAULT_REALM = "streaming";
export const DEFAULT_CLIENT_ID = "streaming-web";

/**
 * Seules ces variables de construction sont lues : jamais `import.meta.env`
 * entier, qui recopierait dans la page toute variable `VITE_*` présente.
 */
const BUILD_ENV: Record<string, string | undefined> = {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_WS_BASE_URL: import.meta.env.VITE_WS_BASE_URL,
  VITE_KEYCLOAK_REALM_URL: import.meta.env.VITE_KEYCLOAK_REALM_URL,
  VITE_KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  VITE_AUTH_REDIRECT_URI: import.meta.env.VITE_AUTH_REDIRECT_URI,
};

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

export function resolveRuntimeConfig(
  raw: RawRuntimeConfig | undefined,
  env: Record<string, string | undefined>,
  location: Pick<Location, "origin" | "protocol" | "host">,
): RuntimeConfig {
  const source: RawRuntimeConfig = raw !== null && typeof raw === "object" ? raw : {};
  const wsScheme = location.protocol === "https:" ? "wss:" : "ws:";
  return {
    apiBaseUrl: text(source.apiBaseUrl) ?? text(env.VITE_API_BASE_URL) ?? "/api/v1",
    wsBaseUrl:
      text(source.wsBaseUrl) ?? text(env.VITE_WS_BASE_URL) ?? `${wsScheme}//${location.host}/api/v1`,
    keycloakRealmUrl:
      text(source.keycloakRealmUrl) ??
      text(env.VITE_KEYCLOAK_REALM_URL) ??
      `${location.origin}/auth/realms/${DEFAULT_REALM}`,
    keycloakClientId:
      text(source.keycloakClientId) ?? text(env.VITE_KEYCLOAK_CLIENT_ID) ?? DEFAULT_CLIENT_ID,
    authRedirectUri:
      text(source.authRedirectUri) ?? text(env.VITE_AUTH_REDIRECT_URI) ?? `${location.origin}/callback`,
  };
}

export const runtimeConfig: RuntimeConfig = resolveRuntimeConfig(
  typeof window === "undefined" ? undefined : window.__STREAMING_CONFIG__,
  BUILD_ENV,
  typeof window === "undefined"
    ? { origin: "http://localhost", protocol: "http:", host: "localhost" }
    : window.location,
);
